// Globals
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'antd/es/form/Form';

// Models
import { IOrderProduct } from '@/models/orders.model';

// Components
import { StyledContentBlock } from '../Styled';
import {
  StyledSupplierWrapper,
  StyledSupplierBock,
  StyledSupplierName,
  StyledPartnerConfirmationInfo,
} from './OrderProductsListStyles';
import { getOrdersTableColumns } from './columns';
import { Table } from '@/ui';
import { IFormInstance } from '@/components';

// Helpers
import { OrderDetailsFormFieldsNames, useUpdateProductsAndCommentsFormRules } from '../../helpers';
import { DateFormats } from '@/constants/dates';
import { formatDate } from '@/utils';

interface IOrderProductsListProps {
  supplierName: string;
  orderProducts: IOrderProduct[];
  handleDelete: (item: IOrderProduct) => void;
  isLoading: boolean;
  form: IFormInstance;
  isEditable: boolean;
  isPartnerConfirmed?: boolean;
  partnerConfirmedByName?: string | null;
  partnerConfirmedAt?: string | null;
}

const OrderProductsList = ({
  supplierName,
  orderProducts,
  isLoading,
  handleDelete,
  form,
  isEditable,
  isPartnerConfirmed = false,
  partnerConfirmedByName,
  partnerConfirmedAt,
}: IOrderProductsListProps) => {
  const { t } = useTranslation(['orders']);
  const validationRules = useUpdateProductsAndCommentsFormRules(t);
  const productsValidationRules = validationRules[OrderDetailsFormFieldsNames.PRODUCTS].default;
  const columns = getOrdersTableColumns({ t, handleDelete, productsValidationRules, isEditable });
  const watchedProductValues = useWatch([OrderDetailsFormFieldsNames.PRODUCTS], form);

  const rowClassName = useCallback(
    (record: IOrderProduct) => {
      const inputValue = watchedProductValues?.[record.id];
      const isInvalid = inputValue !== undefined && inputValue > record.stock_quantity;
      return isInvalid ? 'invalid' : '';
    },
    [watchedProductValues],
  );

  const confirmationText = isPartnerConfirmed
    ? [
        partnerConfirmedByName
          ? t('order_partner_confirmation_by', { name: partnerConfirmedByName })
          : t('orders_partner_confirmation_confirmed'),
        partnerConfirmedAt
          ? t('order_partner_confirmation_at', {
              date: formatDate(partnerConfirmedAt, DateFormats.EUROPEAN),
            })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : t('order_partner_confirmation_pending');

  return (
    <StyledContentBlock>
      <StyledSupplierWrapper>
        <StyledSupplierBock>{t('order_products_list_supplier')}</StyledSupplierBock>
        <StyledSupplierName>{supplierName}</StyledSupplierName>
        <StyledPartnerConfirmationInfo $isConfirmed={isPartnerConfirmed}>
          {confirmationText}
        </StyledPartnerConfirmationInfo>
      </StyledSupplierWrapper>
      <Table
        columns={columns}
        dataSource={orderProducts || []}
        rowKey="id"
        loading={isLoading}
        rowClassName={rowClassName}
      />
    </StyledContentBlock>
  );
};

export default OrderProductsList;
